import type { TechDef } from "./types";

/**
 * PROTOTYPE OVERLAY — prototype-side constants for torch/brazier visuals.
 *
 * The brazier's gameplay def now comes from the data mirror
 * (public/data/buildings_rome.json -> "signal_brazier": Signal Brazier /
 * Foculus, Rome, military, Echelon I, 30 iron + 20 timber, 12s build,
 * 260 HP, requires tech_vigiliae, vision 52.0, vision_height 1.35).
 * Never hand-edit the mirror; never reintroduce a prototype building def —
 * data flows one way from LegionnaireRTS/Legionnaire.
 *
 * What stays here (and why):
 * - BRAZIER_ID: the canonical stable save identifier "signal_brazier".
 *   Old prototype saves used the invented id "brazier"; main.ts remaps
 *   that to BRAZIER_ID on load.
 * - BRAZIER_LIGHT: light radii the brazier casts at night/day. UE data has
 *   no light fields (only vision/vision_height/occluder_height), so the
 *   warm glow that lights nearby ground at night stays a prototype visual.
 *   (The hand torch has no UE data at all yet — only an art_manifest
 *   mention — so torch visuals are prototype-side too.)
 * - PROTOTYPE_TECHS: gameplay mechanics the prototype needs before UE data
 *   adopts them. GameDB injects these into db.technologies AFTER the mirror
 *   load and never overwrites a canonical id. Every entry is marked
 *   PROTOTYPE in its description and must read as "pending UE data".
 */

/** Canonical stable save identifier for the Signal Brazier. Never rename. */
export const BRAZIER_ID = "signal_brazier";

/**
 * Light radii (metres) the brazier casts. Sidecar to the data def so both
 * the torch render layer and the fog-of-war integration can read it.
 * night = full brazier glow, day = faint ember.
 */
export const BRAZIER_LIGHT = { night: 50, day: 15 } as const;

/**
 * Dilectus I–III — PROTOTYPE tech line, pending UE data adoption.
 *
 * The Roman levy (dilectus): the mechanism by which Rome scaled its field
 * army without multiplying headquarters. Each tier adds +50% military
 * command from buildings (additive: ×1.5 / ×2.0 / ×2.5 at I / II / III),
 * so research — not more buildings — carries army growth into the late
 * game. Echelon-gated E2/E3/E4; chained; researched at each playable
 * faction's Echelon II command building (principia / gaul_oppidum_hall /
 * samnite_hill_fort).
 *
 * Historically flavored per the user's rule (no invented tech): the levy
 * is authentically Roman. Gaul/Samnite equivalents from their own
 * traditions are a UE-data follow-up — the mechanic is faction-generic in
 * the prototype so all three playable factions can use it.
 *
 * Costs follow same-echelon precedents from technologies.json.
 */
export const PROTOTYPE_TECHS: TechDef[] = [
  {
    id: "proto_dilectus_1",
    name_en: "Dilectus I",
    echelon: 2,
    cost: { food: 200, denarii: 150 },
    research_time: 60,
    requires: { techs: [], buildings: [] },
    researched_at: ["principia", "gaul_oppidum_hall", "samnite_hill_fort"],
    effects: [{ type: "command_capacity", add: 0.5 }],
    extra: {
      prototype: true,
      name_la: "Dilectus",
      description:
        "PROTOTYPE — pending UE data. The Roman levy: +50% military command from buildings.",
    },
  },
  {
    id: "proto_dilectus_2",
    name_en: "Dilectus II",
    echelon: 3,
    cost: { timber: 200, iron: 120, denarii: 180 },
    research_time: 90,
    requires: { techs: ["proto_dilectus_1"], buildings: [] },
    researched_at: ["principia", "gaul_oppidum_hall", "samnite_hill_fort"],
    effects: [{ type: "command_capacity", add: 0.5 }],
    extra: {
      prototype: true,
      name_la: "Dilectus",
      description:
        "PROTOTYPE — pending UE data. Extended levy: +50% military command from buildings (total +100%).",
    },
  },
  {
    id: "proto_dilectus_3",
    name_en: "Dilectus III",
    echelon: 4,
    cost: { denarii: 800, stone: 300 },
    research_time: 120,
    requires: { techs: ["proto_dilectus_2"], buildings: [] },
    researched_at: ["principia", "gaul_oppidum_hall", "samnite_hill_fort"],
    effects: [{ type: "command_capacity", add: 0.5 }],
    extra: {
      prototype: true,
      name_la: "Dilectus",
      description:
        "PROTOTYPE — pending UE data. Standing levy: +50% military command from buildings (total +150%).",
    },
  },
];
